// Demo product spec + hotspot map for the live playground. Each
// "post image" can have one or more hotspots, visible pins on the
// image that, when clicked, open AddToCartModal with the product
// details. Pure presentation, no backend; the modal's "add to bag"
// just shows a success state.

export type DemoProduct = {
  id: string
  name: string
  brand: string
  price: string
  /** Optional MSRP shown struck-through next to the sale price. */
  compareAt?: string
  /** Optional badge shown next to the price, e.g. "25% off". */
  discountLabel?: string
  currency?: string
  image: string // square product photo for the modal
  blurb: string
  rating: number
  reviews: number
  options?: { label: string; values: string[] }[]
}

export type Hotspot = {
  // Position in % of the parent image (0-100). Spread ~30/70 or so.
  x: number
  y: number
  productId: string
}

export type DemoPost = {
  image: string
  caption: string
  user: string
  likes: number
  comments: number
  hotspots: Hotspot[]
  /** Optional looping MP4, when set, the tile renders as a muted,
   *  autoplay, looping video instead of the static photo. Falls back
   *  to `image` on error. Tolstoy-style shoppable preview. */
  videoSrc?: string
  /** Optional deep link to the original public post (e.g.
   *  https://instagram.com/p/Cxyz...). Shown as a "View original post"
   *  link in the modal's source-attribution block. */
  originalUrl?: string
}

const productImages: Record<string, string> = {
  // Stable Unsplash photo IDs paired with each demo product.
  fashion_1: 'photo-1591047139829-d91aecb6caea', // neutral overshirt/jacket on hanger (prior ID 404'd on Unsplash)
  fashion_2: 'photo-1542060748-10c28b62716f',
  fashion_3: 'photo-1539109136881-3be0616acf4b',
  beauty_1: 'photo-1556228720-195a672e8a03',
  beauty_2: 'photo-1571781926291-c477ebfd024b',
  jewel_1: 'photo-1611591437281-460bfbe1220a',
  jewel_2: 'photo-1605100804763-247f67b3557e',
  home_1: 'photo-1505691938895-1758d7feb511',
  home_2: 'photo-1556909114-f6e7ad7d3136',
  furn_1: 'photo-1555041469-a586c61ea9bc',
  furn_2: 'photo-1538688525198-9b88f6f53126',
  hosp_1: 'photo-1566073771259-6a8506099945',
  hosp_2: 'photo-1551882547-ff40c63fe5fa',
  elec_1: 'photo-1505740420928-5e560c06d30e',
  elec_2: 'photo-1518770660439-4636190af475',
  sport_1: 'photo-1571902943202-507ec2618e8f',
  sport_2: 'photo-1530549387789-4c1017266635',
}

function img(id: string, w = 600, h = 600) {
  return `https://images.unsplash.com/${productImages[id] || productImages.fashion_1}?auto=format&fit=crop&w=${w}&h=${h}&q=80`
}

export const demoProducts: Record<string, DemoProduct> = {
  // Fashion
  linen_overshirt: {
    id: 'linen_overshirt',
    name: 'Linen overshirt. Sage',
    brand: 'Northwave',
    price: '£89',
    image: img('fashion_1'),
    blurb: 'Soft, breathable linen with a relaxed cut. Fits true to size. Made in Portugal.',
    rating: 4.9,
    reviews: 312,
    options: [
      { label: 'Size', values: ['XS', 'S', 'M', 'L', 'XL'] },
      { label: 'Colour', values: ['Sage', 'Cream', 'Black'] },
    ],
  },
  high_rise_jeans: {
    id: 'high_rise_jeans',
    name: 'High-rise jeans. Indigo',
    brand: 'Northwave',
    price: '$118',
    image: img('fashion_2'),
    blurb:
      'Vintage-feel denim, wide-leg cut, organic cotton. Sized up half a size for a relaxed fit.',
    rating: 4.8,
    reviews: 487,
    options: [{ label: 'Size', values: ['24', '26', '28', '30', '32'] }],
  },
  // Skincare
  alaska_salmon_serum: {
    id: 'alaska_salmon_serum',
    name: 'Alaska Salmon PDRN Serum',
    brand: 'Sungboon Editor',
    price: '$48',
    image: '/demo/products/skin-care-slide-2.jpg',
    blurb:
      'Revitalizing serum with PDRN complex. Repairs skin barrier and boosts radiance in 2 weeks.',
    rating: 4.9,
    reviews: 824,
  },
  alaska_salmon_cream: {
    id: 'alaska_salmon_cream',
    name: 'Alaska Salmon PDRN Barrier Cream',
    brand: 'Sungboon Editor',
    price: '$56',
    image: '/demo/products/skin-care-slide-2.jpg',
    blurb: 'Ultra-moisturising barrier cream with PDRN. Soothes sensitive skin overnight.',
    rating: 4.8,
    reviews: 612,
  },

  // Fashion
  parisian_two_piece: {
    id: 'parisian_two_piece',
    name: 'The Parisian Two-Piece Set',
    brand: 'Atelier de Fashion',
    price: '$290',
    image: '/demo/products/fashion-slide-1.jpg',
    blurb:
      'Tailored waistcoat and skirt in signature cream-white weave. Asymmetric detail and gold accents.',
    rating: 4.9,
    reviews: 187,
    options: [{ label: 'Size', values: ['XS', 'S', 'M', 'L'] }],
  },

  classic_sunglasses: {
    id: 'classic_sunglasses',
    name: 'Classic Square Sunglasses. Black',
    brand: 'Atelier de Fashion',
    price: '$74',
    image: '/demo/products/fashion-2-slide-1.jpg',
    blurb:
      'Polished acetate frame with UV400 smoke lenses. Timeless square silhouette, lightweight fit.',
    rating: 4.8,
    reviews: 293,
    options: [{ label: 'Colour', values: ['Black', 'Tortoise', 'Clear'] }],
  },

  // Jewellery
  aethel_otd_watch: {
    id: 'aethel_otd_watch',
    name: 'OTD Gold Mesh Watch',
    brand: 'Aethel',
    price: '$195',
    image: '/demo/products/jewellery-slide-3.jpg',
    blurb: 'Vintage-inspired gold mesh watch. Slim case, mineral crystal. Wear alone or layered.',
    rating: 4.9,
    reviews: 341,
  },
  aethel_otd_cuff: {
    id: 'aethel_otd_cuff',
    name: 'OTD Silver Cuff',
    brand: 'Aethel',
    price: '$85',
    image: '/demo/products/jewellery-slide-3.jpg',
    blurb: 'Sterling silver open cuff. Brushed finish, adjustable fit. Pairs with the gold pieces.',
    rating: 4.8,
    reviews: 218,
  },
  trench_coat: {
    id: 'trench_coat',
    name: 'Trench coat. Camel',
    brand: 'Northwave',
    price: '$240',
    image: img('fashion_3'),
    blurb: 'Belted trench in cotton-twill. Lightweight enough for spring, warm enough for autumn.',
    rating: 4.7,
    reviews: 198,
    options: [{ label: 'Size', values: ['XS', 'S', 'M', 'L'] }],
  },

  // Beauty
  vit_c_serum: {
    id: 'vit_c_serum',
    name: 'Vitamin C serum, 30ml',
    brand: 'Lumen',
    price: '$42',
    image: img('beauty_1'),
    blurb: 'Encapsulated 12% vitamin C with vitamin E and ferulic acid. Brightens in 7 days.',
    rating: 4.9,
    reviews: 1240,
  },
  hydrating_toner: {
    id: 'hydrating_toner',
    name: 'Hydrating toner, 200ml',
    brand: 'Lumen',
    price: '£28',
    image: img('beauty_2'),
    blurb: 'Hyaluronic acid + niacinamide. Use morning + night after cleansing.',
    rating: 4.8,
    reviews: 642,
  },

  // Jewellery
  solitaire_pendant: {
    id: 'solitaire_pendant',
    name: 'Solitaire pendant, 18ct',
    brand: 'Kindred',
    price: '$390',
    image: img('jewel_1'),
    blurb: 'A 0.5ct lab-grown diamond on an 18ct gold chain. Hypoallergenic.',
    rating: 5.0,
    reviews: 96,
    options: [{ label: 'Chain length', values: ['16"', '18"', '20"'] }],
  },
  birthstone_necklace: {
    id: 'birthstone_necklace',
    name: 'Birthstone necklace',
    brand: 'Kindred',
    price: '$210',
    image: img('jewel_2'),
    blurb: 'Personalised birthstone in 14ct gold. Add up to 3 stones for a layered look.',
    rating: 4.9,
    reviews: 312,
  },

  // Home
  linen_bedding: {
    id: 'linen_bedding',
    name: 'Linen bedding. Sage',
    brand: 'Cove·co',
    price: '$220',
    image: img('home_1'),
    blurb: 'Stone-washed French linen. Softens beautifully with every wash. Set of 4.',
    rating: 4.8,
    reviews: 894,
    options: [{ label: 'Size', values: ['Single', 'Double', 'King', 'Super King'] }],
  },
  fig_candle: {
    id: 'fig_candle',
    name: 'Hand-poured candle. Fig',
    brand: 'Cove·co',
    price: '£34',
    image: img('home_2'),
    blurb: 'Soy-wax candle with cotton wick. 60-hour burn time. Scent: green fig + cedar.',
    rating: 4.9,
    reviews: 412,
  },

  // Furniture
  walnut_chair: {
    id: 'walnut_chair',
    name: 'Lounge chair. Walnut',
    brand: 'Studio B',
    price: '$1,290',
    image: img('furn_1'),
    blurb: 'Solid walnut frame with bouclé upholstery. Hand-built in Porto.',
    rating: 4.9,
    reviews: 64,
  },
  oak_table: {
    id: 'oak_table',
    name: 'Oak coffee table',
    brand: 'Studio B',
    price: '£780',
    image: img('furn_2'),
    blurb: 'European oak with a natural matte finish. Built to last decades.',
    rating: 4.8,
    reviews: 88,
  },

  // Hospitality
  rooftop_suite: {
    id: 'rooftop_suite',
    name: 'Rooftop suite, 2 night',
    brand: 'Paloma Stays',
    price: 'from $720',
    image: img('hosp_1'),
    blurb: 'Sea-view king suite with private terrace. Includes breakfast + welcome drink.',
    rating: 4.9,
    reviews: 211,
  },
  private_dining: {
    id: 'private_dining',
    name: 'Private dining. Tasting',
    brand: 'Paloma Stays',
    price: 'from $240',
    image: img('hosp_2'),
    blurb: '8-course tasting journey. Sommelier pairing optional.',
    rating: 4.8,
    reviews: 134,
  },

  // Electronics
  anc_headphones: {
    id: 'anc_headphones',
    name: 'Wireless ANC headphones',
    brand: 'Arc Electronics',
    price: '$349',
    image: img('elec_1'),
    blurb: '40h battery, hybrid noise cancellation, multi-point pairing.',
    rating: 4.7,
    reviews: 1840,
    options: [{ label: 'Colour', values: ['Black', 'Sand'] }],
  },
  studio_mic: {
    id: 'studio_mic',
    name: 'Studio mic. USB-C',
    brand: 'Arc Electronics',
    price: '$179',
    image: img('elec_2'),
    blurb: 'Condenser mic with cardioid + omni patterns. Plug-and-play, no driver.',
    rating: 4.8,
    reviews: 642,
  },

  // Sport
  trail_runner: {
    id: 'trail_runner',
    name: 'Trail runner. Mid',
    brand: 'Roam Outdoors',
    price: '$165',
    image: img('sport_1'),
    blurb: 'Vibram outsole, breathable mesh, water-resistant.',
    rating: 4.8,
    reviews: 906,
    options: [{ label: 'Size', values: ['7', '8', '9', '10', '11', '12'] }],
  },
  yoga_mat: {
    id: 'yoga_mat',
    name: 'Yoga mat, 6mm',
    brand: 'Roam Outdoors',
    price: '$78',
    image: img('sport_2'),
    blurb: 'Cork-and-natural-rubber mat. Antimicrobial, non-slip when wet.',
    rating: 4.9,
    reviews: 312,
  },

  // Pet care
  joint_kibble: {
    id: 'joint_kibble',
    name: 'Joint-support kibble · 8 kg',
    brand: 'Bramble & Co',
    price: '£64',
    image:
      'https://images.unsplash.com/photo-1601758125946-6ec2ef64daf8?auto=format&fit=crop&w=600&h=600&q=80',
    blurb: 'Glucosamine + omega-3 kibble for medium-to-large dogs 5+ years. Vet-formulated.',
    rating: 4.8,
    reviews: 482,
    options: [{ label: 'Size', values: ['2 kg', '4 kg', '8 kg', '12 kg'] }],
  },
  coat_shampoo: {
    id: 'coat_shampoo',
    name: 'Coat-restore shampoo · 500ml',
    brand: 'Bramble & Co',
    price: '£24',
    image:
      'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?auto=format&fit=crop&w=600&h=600&q=80',
    blurb: 'Oat-protein + aloe shampoo for sensitive skin. Tear-free, hypoallergenic.',
    rating: 4.9,
    reviews: 312,
  },

  // Baby + kids
  weaning_set: {
    id: 'weaning_set',
    name: 'Weaning starter set',
    brand: 'Linden & Co',
    price: '£38',
    image:
      'https://images.unsplash.com/photo-1492725764893-90b379c2b6e7?auto=format&fit=crop&w=600&h=600&q=80',
    blurb: 'Bamboo bowl + suction base + first-spoon set. Dishwasher-safe, BPA-free.',
    rating: 4.9,
    reviews: 612,
    options: [{ label: 'Age', values: ['6-9 mo', '9-12 mo', '12-18 mo'] }],
  },
  baby_carrier: {
    id: 'baby_carrier',
    name: 'Hip-seat carrier · 6-36m',
    brand: 'Linden & Co',
    price: '£128',
    image:
      'https://images.unsplash.com/photo-1607344645866-009c320b63e0?auto=format&fit=crop&w=600&h=600&q=80',
    blurb: 'Ergonomic hip-seat for 6-36 months. Two carry positions, machine-washable.',
    rating: 4.8,
    reviews: 412,
    options: [{ label: 'Colour', values: ['Slate', 'Sand', 'Olive'] }],
  },
}

// Per-category UGC photo pools. Each pool has 6+ distinct Unsplash
// IDs that genuinely match the category, so a 6-tile "shoppable wall"
// preview never repeats and never lands on the wrong-category generic
// model that the old `category: 'ugc'` lottery produced (e.g. the
// male athletic torso showing up across every jewellery tile).
// Fashion pool, re-verified 2026-05-13 after dup'd X-app fallbacks
// showed on /widgets/instagram. Cross-referenced against the
// placeholder-images.ts fashion pool (which IS rendering everywhere
// else) so we know each ID still resolves on the Unsplash CDN.
// Self-hosted (public/marketing/fashion/) instead of hotlinked Unsplash IDs,
// so this pool can't 404 out from under us again. Picked for no visible
// brand logos/product IP: candid outfit shots + flatlays only.
const FASHION_UGC = [
  '/marketing/fashion/fashion-20.webp', // candid outfit, neon sign backdrop
  '/marketing/fashion/fashion-08.webp', // floral wrap dress, seaside
  '/marketing/fashion/fashion-01.webp', // flatlay: sneakers + striped tee
  '/marketing/fashion/fashion-18.webp', // hanging rail, boho tops
  '/marketing/fashion/fashion-14.webp', // flatlay: knits + jeans + watch
  '/marketing/fashion/fashion-05.webp', // clothing rack close-up
]
const BEAUTY_UGC = [
  'photo-1631730486572-226d1f595b68',
  'photo-1571781926291-c477ebfd024b',
  'photo-1556228720-195a672e8a03', // was photo-1522335789203…, that ID was 404ing on the beauty industry page
  'photo-1503236823255-94609f598e71',
  'photo-1457972729786-0411a3b2b626',
  'photo-1596462502278-27bfdc403348',
]
const JEWELRY_UGC = [
  'photo-1611591437281-460bfbe1220a', // rings stack
  'photo-1617038220319-276d3cfab638', // gold chain on neck
  'photo-1599643478518-a784e5dc4c8f', // earrings closeup
  'photo-1600003263720-95b45a4035d5', // necklace styled
  'photo-1605100804763-247f67b3557e', // bracelet stack
  'photo-1515562141207-7a88fb7ce338', // pendant on chain
]
const HOME_UGC = [
  'photo-1505691938895-1758d7feb511',
  'photo-1556909114-f6e7ad7d3136',
  'photo-1493663284031-b7e3aefcae8e',
  'photo-1567538096630-e0c55bd6374c',
  'photo-1600121848594-d8644e57abab',
  'photo-1586023492125-27b2c045efd7',
]
// Furniture pool, re-verified 2026-05-13 after the previous IDs
// started returning X-app fallbacks on /industries/furniture. The
// last entry (photo-1586023492125-27b2c045efd7) was the worst
// offender; replaced with a fresh, widely-used interior set.
const FURNITURE_UGC = [
  'photo-1540574163026-643ea20ade25', // bright bedroom + sofa
  'photo-1555041469-a586c61ea9bc', // designer sofa
  'photo-1538688525198-9b88f6f53126', // walnut chair
  'photo-1567538096630-e0c55bd6374c', // dining table
  'photo-1493663284031-b7e3aefcae8e', // living room
  'photo-1565538810643-b5bdb714032a', // modern grey sofa
  'photo-1592078615290-033ee584e267', // wood + brass pendant
  'photo-1505693416388-ac5ce068fe85', // apartment interior
]
const HOSPITALITY_UGC = [
  'photo-1566073771259-6a8506099945',
  'photo-1582719508461-905c673771fd',
  'photo-1551882547-ff40c63fe5fa',
  'photo-1455587734955-081b22074882',
  'photo-1564501049412-61c2a3083791',
  'photo-1542314831-068cd1dbfeeb',
]
const ELECTRONICS_UGC = [
  'photo-1505740420928-5e560c06d30e',
  'photo-1518770660439-4636190af475',
  'photo-1519389950473-47ba0277781c',
  'photo-1496181133206-80ce9b88a853',
  'photo-1517336714731-489689fd1ca8',
  'photo-1593642632559-0c6d3fc62b89',
]
const SPORT_UGC = [
  'photo-1517649763962-0c623066013b',
  'photo-1571902943202-507ec2618e8f',
  'photo-1551698618-1dfe5d97d256',
  'photo-1530549387789-4c1017266635',
  'photo-1541534741688-6078c6bfb5c5',
  'photo-1599058917212-d750089bc07e',
]

// Per-channel demo posts. Each post pairs a category-pinned lifestyle
// photo with 1-2 hotspots that map to demoProducts.
//
// A handful of posts carry an optional videoSrc, when present, the
// preview tile autoplays a muted loop and shows a "LIVE" pip in the
// corner (Tolstoy-style shoppable video). Tiles fall back to the
// static photo if the video fails to load, so a 404'd CDN never
// leaves a black square.
//
// VIDEO_CDN points at Pexels' public-file CDN. Drop in self-hosted
// /videos/*.mp4 paths here when you have first-party assets, the
// component will use whatever URL is provided.
const VIDEO_CDN = {
  fashionTryOn: 'https://videos.pexels.com/video-files/5710143/5710143-sd_506_960_25fps.mp4', // model trying on outfit
  beautyFlatlay: 'https://videos.pexels.com/video-files/4787456/4787456-sd_540_960_24fps.mp4', // cosmetics flatlay
  jewelryHand: 'https://videos.pexels.com/video-files/5961707/5961707-sd_540_960_30fps.mp4', // jewelry on hand close-up
  homeStyling: 'https://videos.pexels.com/video-files/6985137/6985137-sd_506_960_25fps.mp4', // home styling
  sportRunning: 'https://videos.pexels.com/video-files/4763824/4763824-sd_506_960_25fps.mp4', // running outdoors
  furnitureRoom: 'https://videos.pexels.com/video-files/7414106/7414106-sd_506_960_25fps.mp4', // living-room walkthrough
} as const

function fashionPosts(_seed: string): DemoPost[] {
  return [
    {
      image: FASHION_UGC[0],
      user: '@maya_in_london',
      likes: 412,
      comments: 28,
      caption: '"Best fit I’ve owned": wearing the Linen overshirt + High-rise jeans',
      hotspots: [
        { x: 36, y: 48, productId: 'linen_overshirt' },
        { x: 58, y: 72, productId: 'high_rise_jeans' },
      ],
      videoSrc: VIDEO_CDN.fashionTryOn,
    },
    {
      image: FASHION_UGC[1],
      user: '@ava_nyc',
      likes: 298,
      comments: 19,
      caption: 'Trip uniform 🧳',
      hotspots: [{ x: 50, y: 50, productId: 'high_rise_jeans' }],
    },
    {
      image: FASHION_UGC[2],
      user: '@priya_styles',
      likes: 540,
      comments: 64,
      caption: 'My uniform now',
      hotspots: [{ x: 42, y: 40, productId: 'linen_overshirt' }],
    },
    {
      image: FASHION_UGC[3],
      user: '@dani_fits',
      likes: 188,
      comments: 12,
      caption: 'Trench-coat weather',
      hotspots: [{ x: 50, y: 55, productId: 'trench_coat' }],
    },
    {
      image: FASHION_UGC[4],
      user: '@zoe_berlin',
      likes: 327,
      comments: 22,
      caption: 'Day-to-night shoot',
      hotspots: [{ x: 35, y: 45, productId: 'linen_overshirt' }],
    },
    {
      image: FASHION_UGC[5],
      user: '@sam_capsule',
      likes: 244,
      comments: 18,
      caption: 'Capsule wardrobe pick of the season',
      hotspots: [{ x: 60, y: 60, productId: 'trench_coat' }],
    },
  ]
}

export function heroHomePosts(): DemoPost[] {
  return [
    {
      image: '/demo/homepage/fashion-slide-1.webm',
      videoSrc: '/demo/homepage/fashion-slide-1.webm',
      user: '@maya_in_london',
      likes: 412,
      comments: 28,
      caption: '"Best fit I\'ve owned"',
      hotspots: [
        { x: 36, y: 48, productId: 'classic_sunglasses' },
        { x: 58, y: 72, productId: 'parisian_two_piece' },
      ],
    },
    {
      image: '/demo/homepage/skin-care-slide-2.webm',
      videoSrc: '/demo/homepage/skin-care-slide-2.webm',
      user: '@ava_nyc',
      likes: 298,
      comments: 19,
      caption: 'Trip uniform 🧳',
      hotspots: [{ x: 50, y: 50, productId: 'alaska_salmon_serum' }],
    },
    {
      image: '/demo/homepage/jewellery.jpeg',
      user: '@priya_styles',
      likes: 540,
      comments: 64,
      caption: 'My uniform now',
      hotspots: [{ x: 42, y: 40, productId: 'aethel_otd_watch' }],
    },
  ]
}
function beautyPosts(_seed: string): DemoPost[] {
  return [
    {
      image: unsplash(BEAUTY_UGC[0]),
      user: '@lara_glow',
      likes: 612,
      comments: 45,
      caption: 'Glow in 7 days: Vit-C serum',
      hotspots: [{ x: 50, y: 50, productId: 'vit_c_serum' }],
      videoSrc: VIDEO_CDN.beautyFlatlay,
    },
    {
      image: unsplash(BEAUTY_UGC[1]),
      user: '@iris_skincare',
      likes: 388,
      comments: 32,
      caption: 'Better than the £80 one',
      hotspots: [{ x: 42, y: 45, productId: 'hydrating_toner' }],
    },
    {
      image: unsplash(BEAUTY_UGC[2]),
      user: '@naya_routine',
      likes: 514,
      comments: 28,
      caption: 'Morning routine 🌞',
      hotspots: [
        { x: 30, y: 60, productId: 'vit_c_serum' },
        { x: 65, y: 50, productId: 'hydrating_toner' },
      ],
    },
    {
      image: unsplash(BEAUTY_UGC[3]),
      user: '@anaya_glow',
      likes: 222,
      comments: 14,
      caption: 'No white-cast SPF day',
      hotspots: [{ x: 50, y: 50, productId: 'vit_c_serum' }],
    },
    {
      image: unsplash(BEAUTY_UGC[4]),
      user: '@mia_blush',
      likes: 401,
      comments: 25,
      caption: 'Cream blush goes with everything',
      hotspots: [{ x: 50, y: 45, productId: 'hydrating_toner' }],
    },
    {
      image: unsplash(BEAUTY_UGC[5]),
      user: '@lena_routine',
      likes: 178,
      comments: 11,
      caption: 'Saturday self-care',
      hotspots: [{ x: 40, y: 55, productId: 'vit_c_serum' }],
    },
  ]
}
function jewelPosts(_seed: string): DemoPost[] {
  return [
    {
      image: unsplash(JEWELRY_UGC[0]),
      user: '@ren_anniversary',
      likes: 312,
      comments: 22,
      caption: '"Anniversary, kept it on"',
      hotspots: [{ x: 50, y: 50, productId: 'solitaire_pendant' }],
      videoSrc: VIDEO_CDN.jewelryHand,
    },
    {
      image: unsplash(JEWELRY_UGC[1]),
      user: '@sky_layers',
      likes: 198,
      comments: 14,
      caption: 'Wear together / apart',
      hotspots: [{ x: 45, y: 48, productId: 'birthstone_necklace' }],
    },
    {
      image: unsplash(JEWELRY_UGC[2]),
      user: '@liv_jewellery',
      likes: 421,
      comments: 30,
      caption: 'Tennis bracelet · worth the splurge',
      hotspots: [{ x: 55, y: 60, productId: 'solitaire_pendant' }],
    },
    {
      image: unsplash(JEWELRY_UGC[3]),
      user: '@zara_birthstone',
      likes: 286,
      comments: 18,
      caption: '"Mine + mum’s" 💛',
      hotspots: [{ x: 48, y: 52, productId: 'birthstone_necklace' }],
    },
    {
      image: unsplash(JEWELRY_UGC[4]),
      user: '@aya_chain',
      likes: 332,
      comments: 20,
      caption: 'Chunky chain · everyday',
      hotspots: [{ x: 50, y: 45, productId: 'solitaire_pendant' }],
    },
    {
      image: unsplash(JEWELRY_UGC[5]),
      user: '@theo_pearl',
      likes: 174,
      comments: 9,
      caption: 'Pearl drops for the wedding',
      hotspots: [{ x: 50, y: 55, productId: 'birthstone_necklace' }],
    },
  ]
}
function homePosts(_seed: string): DemoPost[] {
  return [
    {
      image: unsplash(HOME_UGC[0]),
      user: '@owen_at_home',
      likes: 488,
      comments: 35,
      caption: '"In our actual bedroom": Linen bedding',
      hotspots: [{ x: 50, y: 50, productId: 'linen_bedding' }],
      videoSrc: VIDEO_CDN.homeStyling,
    },
    {
      image: unsplash(HOME_UGC[1]),
      user: '@jane_candle',
      likes: 312,
      comments: 21,
      caption: 'Fills the whole flat',
      hotspots: [{ x: 50, y: 60, productId: 'fig_candle' }],
    },
    {
      image: unsplash(HOME_UGC[2]),
      user: '@tom_morning',
      likes: 198,
      comments: 15,
      caption: 'Coffee + linen ✨',
      hotspots: [{ x: 48, y: 52, productId: 'linen_bedding' }],
    },
    {
      image: unsplash(HOME_UGC[3]),
      user: '@aya_tokyo',
      likes: 522,
      comments: 39,
      caption: 'Sofa essential · the throw',
      hotspots: [{ x: 55, y: 50, productId: 'fig_candle' }],
    },
    {
      image: unsplash(HOME_UGC[4]),
      user: '@sam_home',
      likes: 401,
      comments: 27,
      caption: 'Apron of the year',
      hotspots: [{ x: 50, y: 60, productId: 'linen_bedding' }],
    },
    {
      image: unsplash(HOME_UGC[5]),
      user: '@maya_diffuser',
      likes: 245,
      comments: 15,
      caption: 'Cedar + tonka diffuser',
      hotspots: [{ x: 50, y: 55, productId: 'fig_candle' }],
    },
  ]
}
function furniturePosts(_seed: string): DemoPost[] {
  return [
    {
      image: unsplash(FURNITURE_UGC[0]),
      user: '@kim_sofa',
      likes: 386,
      comments: 28,
      caption: '"Sat in for 3 years": sofa',
      hotspots: [{ x: 45, y: 55, productId: 'walnut_chair' }],
      videoSrc: VIDEO_CDN.furnitureRoom,
    },
    {
      image: unsplash(FURNITURE_UGC[1]),
      user: '@rui_table',
      likes: 294,
      comments: 22,
      caption: 'Built to last · oak coffee table',
      hotspots: [{ x: 50, y: 60, productId: 'oak_table' }],
    },
    {
      image: unsplash(FURNITURE_UGC[2]),
      user: '@ana_chair',
      likes: 521,
      comments: 38,
      caption: 'Reading chair of dreams',
      hotspots: [{ x: 48, y: 52, productId: 'walnut_chair' }],
    },
    {
      image: unsplash(FURNITURE_UGC[3]),
      user: '@maya_bedframe',
      likes: 222,
      comments: 14,
      caption: 'Sleep changed',
      hotspots: [{ x: 50, y: 55, productId: 'walnut_chair' }],
    },
    {
      image: unsplash(FURNITURE_UGC[4]),
      user: '@nora_brass',
      likes: 312,
      comments: 20,
      caption: 'Hallway hero, brass pendant',
      hotspots: [{ x: 50, y: 40, productId: 'oak_table' }],
    },
    {
      image: unsplash(FURNITURE_UGC[5]),
      user: '@sam_dining',
      likes: 412,
      comments: 31,
      caption: 'Family-of-six approved',
      hotspots: [{ x: 50, y: 60, productId: 'oak_table' }],
    },
  ]
}
function hospitalityPosts(_seed: string): DemoPost[] {
  return [
    {
      image: unsplash(HOSPITALITY_UGC[0]),
      user: '@niko_greece',
      likes: 612,
      comments: 45,
      caption: 'Sunset view 🌅',
      hotspots: [{ x: 50, y: 60, productId: 'rooftop_suite' }],
    },
    {
      image: unsplash(HOSPITALITY_UGC[1]),
      user: '@aya_london',
      likes: 388,
      comments: 32,
      caption: 'Reset day · spa pass',
      hotspots: [{ x: 45, y: 55, productId: 'private_dining' }],
    },
    {
      image: unsplash(HOSPITALITY_UGC[2]),
      user: '@chef_tasting',
      likes: 514,
      comments: 28,
      caption: '8-course chef’s journey',
      hotspots: [{ x: 50, y: 50, productId: 'private_dining' }],
    },
    {
      image: unsplash(HOSPITALITY_UGC[3]),
      user: '@patel_family',
      likes: 222,
      comments: 14,
      caption: 'Kids loved it · family suite',
      hotspots: [{ x: 50, y: 55, productId: 'rooftop_suite' }],
    },
    {
      image: unsplash(HOSPITALITY_UGC[4]),
      user: '@ola_offgrid',
      likes: 401,
      comments: 25,
      caption: 'Off-grid bliss · garden cottage',
      hotspots: [{ x: 50, y: 60, productId: 'rooftop_suite' }],
    },
    {
      image: unsplash(HOSPITALITY_UGC[5]),
      user: '@yoga_weekend',
      likes: 178,
      comments: 11,
      caption: 'Yoga retreat weekend',
      hotspots: [{ x: 50, y: 50, productId: 'private_dining' }],
    },
  ]
}
function electronicsPosts(_seed: string): DemoPost[] {
  return [
    {
      image: unsplash(ELECTRONICS_UGC[0]),
      user: '@devon_audio',
      likes: 612,
      comments: 45,
      caption: 'Beat the bigger brand · ANC headphones',
      hotspots: [{ x: 50, y: 50, productId: 'anc_headphones' }],
    },
    {
      image: unsplash(ELECTRONICS_UGC[1]),
      user: '@lou_keys',
      likes: 388,
      comments: 32,
      caption: 'Typing therapy · mech keyboard',
      hotspots: [{ x: 50, y: 60, productId: 'studio_mic' }],
    },
    {
      image: unsplash(ELECTRONICS_UGC[2]),
      user: '@ana_speaker',
      likes: 222,
      comments: 14,
      caption: 'Sounds way bigger · smart speaker',
      hotspots: [{ x: 50, y: 50, productId: 'anc_headphones' }],
    },
    {
      image: unsplash(ELECTRONICS_UGC[3]),
      user: '@trip_action',
      likes: 401,
      comments: 25,
      caption: 'Trip footage · 4K cam',
      hotspots: [{ x: 50, y: 50, productId: 'studio_mic' }],
    },
    {
      image: unsplash(ELECTRONICS_UGC[4]),
      user: '@sam_podcast',
      likes: 312,
      comments: 20,
      caption: 'Podcast-ready · USB-C mic',
      hotspots: [{ x: 50, y: 50, productId: 'studio_mic' }],
    },
    {
      image: unsplash(ELECTRONICS_UGC[5]),
      user: '@workout_smart',
      likes: 178,
      comments: 11,
      caption: 'Daily companion · smartwatch',
      hotspots: [{ x: 50, y: 50, productId: 'anc_headphones' }],
    },
  ]
}
function sportPosts(_seed: string): DemoPost[] {
  return [
    {
      image: unsplash(SPORT_UGC[0]),
      user: '@avi_trail',
      likes: 612,
      comments: 45,
      caption: '180k logged, no blisters',
      hotspots: [{ x: 50, y: 60, productId: 'trail_runner' }],
      videoSrc: VIDEO_CDN.sportRunning,
    },
    {
      image: unsplash(SPORT_UGC[1]),
      user: '@mia_yoga',
      likes: 388,
      comments: 32,
      caption: 'Best grip I’ve used · cork mat',
      hotspots: [{ x: 50, y: 50, productId: 'yoga_mat' }],
    },
    {
      image: unsplash(SPORT_UGC[2]),
      user: '@reza_marathon',
      likes: 514,
      comments: 28,
      caption: 'Marathon ready · hydration vest',
      hotspots: [{ x: 50, y: 50, productId: 'trail_runner' }],
    },
    {
      image: unsplash(SPORT_UGC[3]),
      user: '@cycle_pro',
      likes: 222,
      comments: 14,
      caption: 'Race-day jersey',
      hotspots: [{ x: 50, y: 55, productId: 'trail_runner' }],
    },
    {
      image: unsplash(SPORT_UGC[4]),
      user: '@strength_bands',
      likes: 401,
      comments: 25,
      caption: 'Travel-kit essential',
      hotspots: [{ x: 50, y: 50, productId: 'yoga_mat' }],
    },
    {
      image: unsplash(SPORT_UGC[5]),
      user: '@crag_day',
      likes: 178,
      comments: 11,
      caption: 'Crag-day pack',
      hotspots: [{ x: 50, y: 50, productId: 'trail_runner' }],
    },
  ]
}

// Stable Unsplash photo IDs for vertical-specific UGC imagery. We can't
// use the placeholder-images.ts `ugc` pool for pet-care and baby-and-
// kids because that pool is mostly fashion/lifestyle photos of adults ,
// the result on /industries/pet-care was random men in turtlenecks
// instead of dogs being fed. These two arrays solve that.
const PET_UGC = [
  'photo-1546975490-e8b92a360b24', // person + dog walking
  'photo-1583337130417-3346a1be7dee', // dog being fed
  'photo-1601758228041-f3b2795255f1', // golden retriever portrait
  'photo-1583511655857-d19b40a7a54e', // puppy
  'photo-1551717743-49959800b1f6', // husky outdoors
  'photo-1574144611937-0df059b5ef3e', // cat on bed
]
const BABY_UGC = [
  'photo-1492725764893-90b379c2b6e7', // baby being fed
  'photo-1519689680058-324335c77eba', // baby sleeping
  'photo-1531123897727-8f129e1688ce', // parent + baby
  'photo-1544126592-807ade215a0b', // toddler crawling on floor (replaces 404'd 1520413322487-8aa7a4d40cad)
  'photo-1607344645866-009c320b63e0', // baby gear
  'photo-1518791841217-8f162f1e1131', // baby on parent shoulder
]
function unsplash(id: string, w = 800, h = 800) {
  return `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&h=${h}&q=80`
}

function petCarePosts(_seed: string): DemoPost[] {
  return [
    {
      image: unsplash(PET_UGC[0]),
      user: '@goldenwithbailey',
      likes: 612,
      comments: 48,
      caption: '6 months on the kibble, coat is unreal',
      hotspots: [{ x: 60, y: 55, productId: 'joint_kibble' }],
    },
    {
      image: unsplash(PET_UGC[1]),
      user: '@dailywithduke',
      likes: 388,
      comments: 28,
      caption: 'No more skipped breakfasts',
      hotspots: [{ x: 50, y: 60, productId: 'joint_kibble' }],
    },
    {
      image: unsplash(PET_UGC[2]),
      user: '@beau_the_lab',
      likes: 514,
      comments: 36,
      caption: 'Before / after coat, shampoo did this in 3 baths',
      hotspots: [{ x: 52, y: 50, productId: 'coat_shampoo' }],
    },
    {
      image: unsplash(PET_UGC[3]),
      user: '@millie.puppy',
      likes: 612,
      comments: 41,
      caption: '8-week-old loves the small-breed mix',
      hotspots: [{ x: 50, y: 55, productId: 'joint_kibble' }],
    },
    {
      image: unsplash(PET_UGC[4]),
      user: '@kona_husky',
      likes: 412,
      comments: 25,
      caption: 'Sensitive-skin pup, finally a shampoo that works',
      hotspots: [{ x: 48, y: 52, productId: 'coat_shampoo' }],
    },
    {
      image: unsplash(PET_UGC[5]),
      user: '@miso_the_cat',
      likes: 322,
      comments: 19,
      caption: 'Cat-approved · grain-free recipe',
      hotspots: [{ x: 55, y: 50, productId: 'joint_kibble' }],
    },
  ]
}

function babyKidsPosts(_seed: string): DemoPost[] {
  return [
    {
      image: unsplash(BABY_UGC[0]),
      user: '@first_spoon',
      likes: 612,
      comments: 48,
      caption: 'Weaning week one · the bowl stays put',
      hotspots: [{ x: 50, y: 55, productId: 'weaning_set' }],
    },
    {
      image: unsplash(BABY_UGC[1]),
      user: '@naptime_diaries',
      likes: 388,
      comments: 28,
      caption: '"He sleeps through the dishwasher now"',
      hotspots: [{ x: 50, y: 55, productId: 'weaning_set' }],
    },
    {
      image: unsplash(BABY_UGC[2]),
      user: '@dad_and_lila',
      likes: 514,
      comments: 36,
      caption: 'Carrier survived three flights',
      hotspots: [{ x: 50, y: 50, productId: 'baby_carrier' }],
    },
    {
      image: unsplash(BABY_UGC[3]),
      user: '@kido.adventures',
      likes: 412,
      comments: 25,
      caption: 'From 9 mo to 2 yrs · same bowl',
      hotspots: [{ x: 50, y: 55, productId: 'weaning_set' }],
    },
    {
      image: unsplash(BABY_UGC[4]),
      user: '@hike_with_baby',
      likes: 322,
      comments: 19,
      caption: 'Hands-free trail walk · carrier won',
      hotspots: [{ x: 50, y: 50, productId: 'baby_carrier' }],
    },
    {
      image: unsplash(BABY_UGC[5]),
      user: '@mama_of_two',
      likes: 286,
      comments: 17,
      caption: 'Carrier #4, first one that actually fits my back',
      hotspots: [{ x: 48, y: 52, productId: 'baby_carrier' }],
    },
  ]
}

// Look up a coherent set of demo posts for a slug. Industries pages
// pass their slug; widget pages can pass a network name. Falls back
// to fashion.
export function demoPostsFor(slug: string): DemoPost[] {
  const s = slug.toLowerCase()
  if (/(pet|dog|cat)/.test(s)) return petCarePosts(slug)
  if (/(baby|kid|child)/.test(s)) return babyKidsPosts(slug)
  if (/(beauty|skin)/.test(s)) return beautyPosts(slug)
  if (/(jewel)/.test(s)) return jewelPosts(slug)
  if (/(home|lifestyle)/.test(s)) return homePosts(slug)
  if (/(furniture|sofa|chair)/.test(s)) return furniturePosts(slug)
  if (/(hospitality|hotel|stay)/.test(s)) return hospitalityPosts(slug)
  if (/(electronic|tech|gadget)/.test(s)) return electronicsPosts(slug)
  if (/(sport|fitness|outdoor)/.test(s)) return sportPosts(slug)
  return fashionPosts(slug)
}

export function getDemoProduct(id: string): DemoProduct | null {
  return demoProducts[id] || null
}
